import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { File, Image, Video, Music, Archive, Download, Link as LinkIcon, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useMedia } from '@/contexts/MediaContext';

const getIconForType = (type) => {
    switch (type) {
        case 'image': return <Image className="w-16 h-16 sm:w-24 sm:h-24 text-cyan-300" />;
        case 'video': return <Video className="w-16 h-16 sm:w-24 sm:h-24 text-blue-300" />;
        case 'audio': return <Music className="w-16 h-16 sm:w-24 sm:h-24 text-sky-300" />;
        case 'gif': return <Archive className="w-16 h-16 sm:w-24 sm:h-24 text-teal-300" />;
        default: return <File className="w-16 h-16 sm:w-24 sm:h-24 text-slate-400" />;
    }
};

const SharePage = () => {
    const { id } = useParams();
    const [mediaItem, setMediaItem] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { toast } = useToast();
    const { getFileById } = useMedia();

    useEffect(() => {
        const loadMedia = async () => {
            setIsLoading(true);
            try {
                const item = await getFileById(id);
                if (item) {
                    setMediaItem(item);
                } else {
                    setMediaItem(null);
                }
            } catch (error) {
                console.error('Erro ao carregar mídia compartilhada:', error);
                setMediaItem(null);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            loadMedia();
        }
    }, [id, getFileById]);

    const handleDownload = () => {
        if (mediaItem?.url) {
            window.open(mediaItem.url, '_blank', 'noopener,noreferrer');
        } else {
             toast({
                title: "Erro ao baixar",
                description: "Link de download indisponível.",
                variant: "destructive",
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-950">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
        );
    }
    
    if (!mediaItem) {
        return (
            <div className="flex items-center justify-center min-h-screen p-4 text-center bg-slate-950">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 p-8 sm:p-12 rounded-2xl max-w-md w-full"
                >
                    <AlertTriangle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
                    <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Mídia Não Encontrada</h1>
                    <p className="text-slate-400 mb-6">O link pode estar quebrado ou a mídia foi removida.</p>
                    <Button asChild className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl w-full py-6">
                        <Link to="/dashboard">Voltar ao Início</Link>
                    </Button>
                </motion.div>
            </div>
        );
    }


    return (
        <>
            <Helmet>
                <title>Compartilhado: {mediaItem.name} | Mídia Azul</title>
                <meta name="description" content={`Veja ou baixe ${mediaItem.name}.`} />
            </Helmet>
            <div className="flex items-center justify-center min-h-screen p-4 bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="w-full max-w-4xl bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/10"
                >
                    <div className="p-8 md:p-12 grid md:grid-cols-2 gap-12 items-center">
                        <div className="flex flex-col items-center justify-center p-8 bg-slate-800/30 rounded-2xl border border-slate-700/50 aspect-square">
                            {getIconForType(mediaItem.type)}
                            <div className="mt-6 text-center">
                                <h2 className="text-xl font-bold text-white mb-1 line-clamp-2">{mediaItem.name}</h2>
                                <p className="text-sm text-slate-400 uppercase tracking-wider">{mediaItem.type}</p>
                            </div>
                        </div>

                        <div className="space-y-8">
                            <div>
                                <h1 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
                                    Arquivo Compartilhado
                                </h1>
                                <p className="text-slate-400 text-lg leading-relaxed">
                                    Você recebeu acesso a este arquivo através do MediaHub. Você pode visualizá-lo ou baixá-lo abaixo.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <Button 
                                    onClick={handleDownload}
                                    className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-cyan-500/20"
                                >
                                    <Download className="mr-2 h-5 w-5" />
                                    Baixar Arquivo
                                </Button>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm text-slate-500">
                                    <div className="flex flex-col p-4 bg-slate-800/30 rounded-xl border border-slate-800">
                                        <span className="text-xs uppercase tracking-wider font-bold mb-1">Tamanho</span>
                                        <span className="text-slate-200 font-medium">{mediaItem.size ? (mediaItem.size / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}</span>
                                    </div>
                                    <div className="flex flex-col p-4 bg-slate-800/30 rounded-xl border border-slate-800">
                                        <span className="text-xs uppercase tracking-wider font-bold mb-1">Data</span>
                                        <span className="text-slate-200 font-medium">{new Date().toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </>
    );
};
export default SharePage;